// createProfile/reducer.js
import { combineReducers } from 'redux';
import reduceReducers from 'reduce-reducers';
import loadingStateReducerFactory from '@nhl/loading-state-reducer-factory';
import changeValueReducerFactory from '@nhl/change-value-reducer-factory';
import genericCollectionUtils from '@nhl/generic-collection-utils';

import { clearErrorsOnActionTypeReducerFactory, clearLoadingStateOnActionReducerFactory } from '../../utils';
import actionTypes from './actionTypes';

/* State Shape
{
    loadingState: {
        saveState: {
            isLoading: bool,
            success: bool,
            errors: Array
        },
        industryTypes: {
            isLoading: bool,
            success: bool,
            errors: Array
        },
        profileInfo: {
            isLoading: bool,
            success: bool,
            errors: Array
        }
    }
    profileState: {
        firstName: String,
        lastName: String,
        title: String,
        email: String,
        password: String,
        confirmPassword: String,
        industryType: {
            digital: bool,
            print: bool,
            broadcast: bool,
            radio: bool
        },
        country: String,
        nonNa: bool,
        address1: String,
        address2: String,
        city: String,
        postalCode: String,
        state: String
    },
    initialProfileState: {
        [Same as profileState, except this is used for undoing changes]
    }
}
*/

const getProfileReducer = changeAction => {
    const firstNameReducer = changeValueReducerFactory('')(changeAction, 'firstName',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const lastNameReducer = changeValueReducerFactory('')(changeAction, 'lastName',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const titleReducer = changeValueReducerFactory('')(changeAction, 'title',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const mediaAffiliationReducer = changeValueReducerFactory('')(changeAction, 'mediaAffiliation',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const emailReducer = changeValueReducerFactory('')(changeAction, 'email',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const passwordReducer = changeValueReducerFactory('')(changeAction, 'password',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const confirmPasswordReducer = changeValueReducerFactory('')(changeAction, 'confirmPassword',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const countryCodeReducer = changeValueReducerFactory('')(changeAction, 'countryCode',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const orgOutsideUsCaReducer = changeValueReducerFactory(false)(changeAction, 'orgOutsideUsCa',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const address1Reducer = changeValueReducerFactory('')(changeAction, 'address1',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const address2Reducer = changeValueReducerFactory('')(changeAction, 'address2',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const cityReducer = changeValueReducerFactory('')(changeAction, 'city',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const postalCodeReducer = changeValueReducerFactory('')(changeAction, 'postalCode',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const stateProvinceCodeReducer = changeValueReducerFactory('')(changeAction, 'stateProvinceCode',
        actionTypes.HYDRATE_PROFILE, 'profile');
    const requestingScreenerAccessReducer = changeValueReducerFactory(false)(changeAction, 'requestScreenerAccess',
        actionTypes.HYDRATE_PROFILE, 'profile');

    const industriesReducer = (state = [], action) => {
        if (action.type === changeAction && action.payload.name === 'industries') {
            const { value } = action.payload;

            return genericCollectionUtils.performAddOrRemoveSelection(state.splice(0), value.id,
                value.availableIndustryTypes, value.isChecked);
        } else if (action.type === actionTypes.HYDRATE_PROFILE) {
            return action.payload.profile.industries || [];
        }

        return state;
    };
    
    return combineReducers({
        firstName: firstNameReducer,
        lastName: lastNameReducer,
        title: titleReducer,
        mediaAffiliation: mediaAffiliationReducer,
        email: emailReducer,
        password: passwordReducer,
        confirmPassword: confirmPasswordReducer,
        countryCode: countryCodeReducer,
        orgOutsideUsCa: orgOutsideUsCaReducer,
        address1: address1Reducer,
        address2: address2Reducer,
        city: cityReducer,
        postalCode: postalCodeReducer,
        stateProvinceCode: stateProvinceCodeReducer,
        requestingScreenerAccess: requestingScreenerAccessReducer,
        industries: industriesReducer
    });
};

const saveStateReducer = loadingStateReducerFactory(
    actionTypes.REQUEST_SAVE,
    actionTypes.REQUEST_REGISTER
)(
    actionTypes.SAVE_RESPONSE,
    actionTypes.REGISTER_RESPONSE
);

const enhancedSaveStateReducer = reduceReducers(saveStateReducer,
    clearErrorsOnActionTypeReducerFactory(actionTypes.CHANGE_VALUE),
    clearLoadingStateOnActionReducerFactory(actionTypes.HYDRATE_PROFILE));

const industryTypesLoadingStateReducer =
    loadingStateReducerFactory(actionTypes.REQUEST_INDUSTRY_TYPES)(actionTypes.INDUSTRY_TYPES_RESPONSE);

const profileInfoLoadingStateReducer =
    loadingStateReducerFactory(actionTypes.REQUEST_ACCOUNT_INFO)(actionTypes.ACCOUNT_INFO_RESPONSE);

const availableIndustryTypesReducer = (state = [], action) => {
    if (action.type === actionTypes.INDUSTRY_TYPES_RESPONSE) {
        return action.payload.industryTypes;
    }

    return state;
};

const loadingStateReducer = combineReducers({
    saveState: enhancedSaveStateReducer,
    industryTypes: industryTypesLoadingStateReducer,
    profileInfo: profileInfoLoadingStateReducer
});

const reducer = combineReducers({
    loadingState: loadingStateReducer,
    profileState: getProfileReducer(actionTypes.CHANGE_VALUE),
    initialProfileState: getProfileReducer(),
    availableIndustryTypes: availableIndustryTypesReducer
});

export default reducer;